import type { IntakeLanguage, IntakeText } from "@duralex/shared";

// The office and the client read names, the wire carries codes.
export const INTAKE_LANGUAGE_NAMES: Record<string, string> = {
  en: "English",
  ru: "Russian",
  uk: "Ukrainian",
  uz: "Uzbek",
  ka: "Georgian",
  tr: "Turkish",
  es: "Spanish",
};

/** English for a language code: the office's list, then ICU, then the code as
    written. fallback "none": the default names "und" root and lower-cases a
    value that is already a name. */
export function languageName(code: string): string {
  const own = INTAKE_LANGUAGE_NAMES[code];
  if (own) return own;
  try {
    return new Intl.DisplayNames(["en"], { type: "language", fallback: "none" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** One language on screen: the text in `locale`, else its English. */
export function textIn(text: IntakeText, locale: string): string {
  return text[locale as IntakeLanguage] || text.en;
}

/** A language named in itself, from the runtime's ICU: "English",
    Russian in Cyrillic, Georgian in Mkhedruli. ICU spells most of them
    in lower case, so the first letter is raised - except Georgian, whose
    capitals exist for emphasis only. Without Intl.DisplayNames, or for a
    code it does not know, the English name. */
export function languageAutonym(code: string): string {
  const english = INTAKE_LANGUAGE_NAMES[code] ?? code;
  let name: string | undefined;
  try {
    name = new Intl.DisplayNames([code], { type: "language" }).of(code);
  } catch {
    return english;
  }
  if (!name || name === code) return english;
  if (/^\p{Script=Georgian}/u.test(name)) return name;
  return name.charAt(0).toLocaleUpperCase(code) + name.slice(1);
}
